import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { 
  Search, 
  X, 
  Filter,
  Building2,
  Eye,
  Users,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { API } from "@/utils/constants";
import { getEffectiveStatus } from "@/utils/helpers";
import StatusBadge from "@/components/common/StatusBadge";
import RuleDetailModal from "@/components/modals/RuleDetailModal";

// Status priority for sorting: Non-Compliant (0) > Over-Compliant (1) > Compliant (2) > Not Evaluated (3)
const getStatusPriority = (status) => {
  if (!status) return 4;
  const normalized = status.toLowerCase();
  if (normalized.includes('non-compliant') || normalized.includes('non compliant')) return 0;
  if (normalized.includes('over-compliant') || normalized.includes('over compliant')) return 1;
  if (normalized === 'compliant') return 2;
  if (normalized.includes('not evaluated')) return 3;
  return 4;
};

const RulesList = () => {
  const [rules, setRules] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: '', status: '', search: '' });
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'overallStatus', direction: 'asc' });

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.state) params.append('state', filters.state);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/compliance/rules?${params}`);
      setRules(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${API}/compliance/states`);
        setStates(response.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchStates();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Handle column sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort the rules based on current sort config
  const sortedRules = useMemo(() => {
    const sorted = [...rules];
    
    sorted.sort((a, b) => {
      let comparison = 0;
      
      switch (sortConfig.key) {
        case 'payRule':
          comparison = (a.payrule || '').localeCompare(b.payrule || '');
          break;
        case 'workRule':
          comparison = (a.workrule || '').localeCompare(b.workrule || '');
          break;
        case 'state':
          comparison = (a.state_name || a.state || '').localeCompare(b.state_name || b.state || '');
          break;
        case 'employees':
          comparison = (a.employee_count || 0) - (b.employee_count || 0);
          break;
        case 'overallStatus':
          const aStatus = getEffectiveStatus(a.overall_compliance_status, a.overall_over_compliance_reason);
          const bStatus = getEffectiveStatus(b.overall_compliance_status, b.overall_over_compliance_reason);
          comparison = getStatusPriority(aStatus) - getStatusPriority(bStatus);
          break;
        case 'payRuleStatus':
          comparison = getStatusPriority(a.payrule_compliance_status) - getStatusPriority(b.payrule_compliance_status);
          break;
        case 'workRuleStatus':
          comparison = getStatusPriority(a.workrule_compliance_status) - getStatusPriority(b.workrule_compliance_status);
          break;
        default:
          comparison = 0;
      }
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [rules, sortConfig]);

  // Sortable header component
  const SortableHeader = ({ label, sortKey, className = '' }) => {
    const isActive = sortConfig.key === sortKey;
    return (
      <th 
        className={`px-4 py-3 cursor-pointer hover:bg-slate-700/50 transition-colors select-none ${className}`}
        onClick={() => handleSort(sortKey)}
        data-testid={`sort-${sortKey}`}
      >
        <div className={`flex items-center gap-1 ${className.includes('text-center') ? 'justify-center' : ''}`}>
          <span>{label}</span>
          {isActive ? (
            sortConfig.direction === 'asc' ? (
              <ArrowUp className="w-3.5 h-3.5 text-[#2EC4B6]" />
            ) : (
              <ArrowDown className="w-3.5 h-3.5 text-[#2EC4B6]" />
            )
          ) : (
            <ArrowUpDown className="w-3.5 h-3.5 text-slate-500" />
          )}
        </div>
      </th>
    );
  };

  return (
    <div data-testid="rules-list-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white font-['Manrope'] tracking-tight">Pay Rules</h1>
        <p className="text-slate-400 mt-2">Browse and analyze all pay rule attestation paths</p>
      </div>

      {/* Filters */}
      <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded-2xl p-5 shadow-sm">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-400 mb-1">Search Rules</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search by rule name..."
                data-testid="search-input"
                className="w-full pl-10 pr-4 py-2 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2EC4B6] focus:border-transparent bg-slate-900 text-white placeholder-slate-500"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>

          {/* State Filter */}
          <div className="w-48">
            <label className="block text-xs font-medium text-slate-400 mb-1">State</label>
            <select
              data-testid="state-filter"
              className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2EC4B6] focus:border-transparent bg-slate-900 text-white"
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
            >
              <option value="">All States</option>
              {states.map(s => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="w-48">
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              data-testid="status-filter"
              className="w-full px-3 py-2 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2EC4B6] focus:border-transparent bg-slate-900 text-white"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="Compliant">Compliant</option>
              <option value="Over-Compliant">Over-Compliant</option>
              <option value="Non-Compliant">Non-Compliant</option>
              <option value="Not Evaluated">Not Evaluated</option>
            </select>
          </div>

          {/* Clear Filters */}
          {(filters.state || filters.status || filters.search) && (
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ state: '', status: '', search: '' })}
                data-testid="clear-filters-btn"
                className="px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">
          <span className="font-medium text-white">{rules.length}</span> rules found
        </p>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Filter className="w-3 h-3" />
          {filters.state || filters.status || filters.search ? 'Filtered results' : 'Showing all rules'}
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EC4B6]"></div>
          </div>
        ) : rules.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <FileText className="w-12 h-12 text-slate-600 mb-3" />
            <p>No rules found matching your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="table-header-row">
                  <SortableHeader label="Pay Rule" sortKey="payRule" className="text-left" />
                  <SortableHeader label="Work Rule" sortKey="workRule" className="text-left" />
                  <SortableHeader label="State" sortKey="state" className="text-left" />
                  <SortableHeader label="Employees" sortKey="employees" className="text-center" />
                  <SortableHeader label="Overall Status" sortKey="overallStatus" className="text-center" />
                  <SortableHeader label="Pay Rule" sortKey="payRuleStatus" className="text-center" />
                  <SortableHeader label="Work Rule" sortKey="workRuleStatus" className="text-center" />
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedRules.map((rule) => (
                  <tr 
                    key={rule.attestation_path_id} 
                    data-testid={`rule-row-${rule.attestation_path_id}`}
                    className="table-row"
                  >
                    <td className="table-cell font-medium text-white">
                      {rule.payrule || '-'}
                    </td>
                    <td className="table-cell text-slate-400">
                      {rule.workrule || '-'}
                    </td>
                    <td className="table-cell">
                      <span className="inline-flex items-center gap-1.5 text-slate-300">
                        <Building2 className="w-3.5 h-3.5 text-slate-500" />
                        {rule.state_name || rule.state || '-'}
                      </span>
                    </td>
                    <td className="table-cell text-center">
                      {rule.employee_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-700 rounded-full text-xs font-medium text-slate-300">
                          <Users className="w-3 h-3" />
                          {rule.employee_count}
                        </span>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="table-cell text-center">
                      <StatusBadge status={getEffectiveStatus(rule.overall_compliance_status, rule.overall_over_compliance_reason)} />
                    </td>
                    <td className="table-cell text-center">
                      <StatusBadge status={rule.payrule_compliance_status} />
                    </td>
                    <td className="table-cell text-center">
                      <StatusBadge status={rule.workrule_compliance_status} />
                    </td>
                    <td className="table-cell text-center">
                      <button
                        onClick={() => setSelectedRuleId(rule.attestation_path_id)}
                        data-testid={`view-rule-${rule.attestation_path_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-[#2EC4B6] hover:bg-[#2EC4B6]/20 rounded-lg transition-colors font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedRuleId && (
        <RuleDetailModal
          ruleId={selectedRuleId}
          onClose={() => setSelectedRuleId(null)}
        />
      )}
    </div>
  );
};

export default RulesList;
